<template>
  <div class="qa-form" :class="{ desktop: !tabletContainer, 'force-tablet': forceTablet }">
    <div v-if="tabletContainer" class="top-header"><span class="bold">Have a question?</span> We're here to help.</div>
    <div v-else class="block">
      <div class="main-text"><span class="bold">Have a question?</span> We're here to help.</div>
    </div>
    <div class="block">
      <div class="icon">
        <i class="af af-call"></i>
      </div>
      <div class="message">
        <div class="main-header">
          <a href="tel:+1-888-746-3455">
            +1-888-746-3455
          </a>
        </div>
        <div class="secondary-header">
          <p v-if="phoneOnlyWeekdays" class="caption">Open Mon-Fri {{ csPhoneWeekdaysHoursStartText }} - {{ csPhoneWeekdaysHoursEndText }}</p>
          <template v-else>
            <p v-if="phoneSameHours" class="caption">7 days a week, {{ csPhoneWeekdaysHoursStartText }} - {{ csPhoneWeekdaysHoursEndText }}</p>
            <div v-else class="caption">
              <span>Mon-Fri {{ csPhoneWeekdaysHoursStartText }} - {{ csPhoneWeekdaysHoursEndText }}</span>
              <br />
              <span>Sat-Sun {{ csPhoneWeekendHoursStartText }} - {{ csPhoneWeekendHoursEndText }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="block" :class="{ tablet: tabletContainer }">
      <div class="icon">
        <i class="af af-chat"></i>
      </div>
      <div class="message">
        <div class="main-header" @click.passive="chat()">
          Live Chat with us
        </div>
        <div class="secondary-header">
          <p v-if="chatOnlyWeekdays" class="caption">Open Mon-Fri {{ csChatWeekdaysHoursStartText }} - {{ csChatWeekdaysHoursEndText }}</p>
          <template v-else>
            <p v-if="chatSameHours" class="caption">7 days a week, {{ csChatWeekdaysHoursStartText }} - {{ csChatWeekdaysHoursEndText }}</p>
            <div v-else class="caption">
              <span>Mon-Fri {{ csChatWeekdaysHoursStartText }} - {{ csChatWeekdaysHoursEndText }}</span>
              <br />
              <span>Sat-Sun {{ csChatWeekendHoursStartText }} - {{ csChatWeekendHoursEndText }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="block">
      <div class="icon">
        <i class="af af-mail"></i>
      </div>
      <div class="message">
        <div class="main-header" @click.passive="onSendMessageClicked()">
          Send us a message
        </div>
        <div class="secondary-header">
          Our current response time is 3 to 4 business days
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { ref, onMounted, onBeforeUnmount } from '@vue/composition-api';
import useSupportHours from '@/compositions/useSupportHours';

export default {
  name: 'QAForm',
  props: {
    forceTablet: {
      type: Boolean,
      default: false,
    },
    contactUsCustomLink: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const tabletContainer = ref(false);

    const {
      csPhoneWeekdaysHoursStartText,
      csPhoneWeekdaysHoursEndText,
      csPhoneWeekendHoursStartText,
      csPhoneWeekendHoursEndText,
      csChatWeekdaysHoursStartText,
      csChatWeekdaysHoursEndText,
      csChatWeekendHoursStartText,
      csChatWeekendHoursEndText,
      phoneOnlyWeekdays,
      chatOnlyWeekdays,
      phoneSameHours,
      chatSameHours,
    } = useSupportHours(context?.root?.$store?.state?.common);

    function onSendMessageClicked() {
      window.location.href = this.contactUsCustomLink || this.$router.resolve({ name: 'message' }).href;
    }

    function setTabletQAFormContainer() {
      if (window.matchMedia('(max-width: 1200px)').matches || props.forceTablet) {
        tabletContainer.value = true;
      } else {
        tabletContainer.value = false;
      }
    }

    onMounted(() => {
      setTabletQAFormContainer();
      window.addEventListener('resize', setTabletQAFormContainer);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', setTabletQAFormContainer);
    });

    return {
      csPhoneWeekdaysHoursStartText,
      csPhoneWeekdaysHoursEndText,
      csPhoneWeekendHoursStartText,
      csPhoneWeekendHoursEndText,
      csChatWeekdaysHoursStartText,
      csChatWeekdaysHoursEndText,
      csChatWeekendHoursStartText,
      csChatWeekendHoursEndText,
      phoneOnlyWeekdays,
      phoneSameHours,
      chatOnlyWeekdays,
      chatSameHours,
      tabletContainer,
      onSendMessageClicked,
      setTabletQAFormContainer,
    };
  },
  methods: {
    ...mapActions('common', ['chat']),
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';

.qa-form {
  border: 1px solid var(--grey-ultra-light);
  box-sizing: border-box;
  border-radius: var(--space-2);
  padding: var(--space-4) var(--space-6) var(--space-2) var(--space-6);
  text-align: center;

  &.desktop {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4) var(--space-6);
  }

  .top-header {
    text-align: center;
    padding-bottom: var(--space-4);
    @media (max-width: $mobile-breakpoint - 1) {
      text-align: left;
      padding-bottom: var(--space-2);
    }
  }

  div.block {
    padding: var(--space-0) var(--space-3);
    display: inline-flex;
    text-align: left;
    align-items: flex-start;
    &:first-child {
      @media (max-width: 1010px) {
        padding-left: var(--space-0);
      }
    }
    .icon {
      width: var(--space-10);
      height: var(--space-10);
      font-size: var(--space-10);
      line-height: var(--space-10);
      color: var(--link-color);
      margin-right: var(--space-4);
    }
    .main-text {
      font-size: var(--space-4);
      line-height: var(--font-size-20);
    }
    .main-header,
    a {
      color: var(--coral);
      text-decoration-line: underline;
      font-size: var(--space-4);
      line-height: 20px;
      cursor: pointer;
    }
    .secondary-header {
      font-size: var(--space-3);
      line-height: var(--font-size-20);
      padding-top: var(--space-1);
      p:not([class^='pxl-p']) {
        margin: var(--space-0);
      }
    }
    @media (max-width: 830px) {
      padding: var(--space-0) var(--space-2);
    }
    @media (max-width: 800px) {
      padding: var(--space-0) var(--space-1);
    }
    @media (max-width: $mobile-breakpoint - 1) {
      padding: var(--space-3) var(--space-0);
      display: flex;
    }
  }

  &.force-tablet {
    margin-top: 0px;
    padding: var(--space-4) var(--space-6);

    .top-header {
      text-align: left;
    }

    div.block {
      display: flex;
      text-align: left;
      align-items: flex-start;
      padding: var(--space-3) var(--space-0);
    }
  }

  @media (max-width: $tablet-breakpoint) {
    padding: var(--space-4) var(--space-6);
  }
}
</style>
